import { gql, useMutation } from '@apollo/client'
import SkuSelectInput from 'components/Dashboard/SkuSelectInput'
import { useToast } from 'components/Dashboard/Toast'
import { Modal2 } from 'components/Modal'
import SubmitButton from 'components/SubmitButton'
import TextInput from 'components/TextInput'
import { Form, Formik } from 'formik'
import { ChangeKitSkuMutation, ChangeKitSkuMutationVariables } from './__generated__/ChangeKitSkuMutation'

const changeKitSkuMutation = gql`
  mutation ChangeKitSkuMutation($input: ChangeKitSkuInput!) {
    changeKitSku(input: $input) {
      success
      message
      kit {
        id
        status
      }
    }
  }
`

interface ChangeKitSkuModalProps {
  kitId: string
  initialSku: string
  onRequestClose: () => void
}

const ChangeKitSkuModal = ({ kitId, initialSku, onRequestClose }: ChangeKitSkuModalProps) => {
  const [changeKitSku] = useMutation<ChangeKitSkuMutation, ChangeKitSkuMutationVariables>(changeKitSkuMutation)

  const { addToast } = useToast()

  return (
    <Modal2 isOpen onRequestClose={onRequestClose} header={`Change Kit SKU (initially: ${initialSku})`}>
      <Formik
        initialValues={{ sku: initialSku, zdRef: '', reason: '' }}
        onSubmit={async (values, { setStatus }) => {
          try {
            const resp = await changeKitSku({
              variables: {
                input: {
                  kitId,
                  sku: values.sku,
                  reason: values.reason,
                  zdRef: values.zdRef,
                },
              },
            })

            if (!resp.data.changeKitSku.success) {
              throw new Error(resp.data.changeKitSku.message || 'Something went wrong')
            }

            onRequestClose()
            addToast({
              messageType: 'success',
              title: 'SKU changed',
              content: (
                <>
                  Kit SKU change from {initialSku} to {values.sku}
                </>
              ),
              timeout: 5000,
            })
          } catch (err) {
            setStatus({ error: err.message })
          }
        }}
      >
        {({ isSubmitting, status, values }) => (
          <Form>
            <SkuSelectInput
              name="sku"
              validate={(value) => {
                return value === initialSku ? 'Must be different from the initial SKU' : undefined
              }}
            />

            <TextInput name="reason" label="Reason for SKU change" />
            <TextInput name="zdRef" label="Zendesk ticket no (if applicable)" />

            <article className="message is-warning">
              <div className="message-body">
                <strong>Be careful! </strong> not all SKU changes make sense.
              </div>
            </article>

            <div className="buttons is-right mt-5">
              <SubmitButton className="is-primary" submitting={isSubmitting} disabled={isSubmitting}>
                Change SKU to {values.sku}
              </SubmitButton>
            </div>
            {status?.error && (
              <article className="message is-danger">
                <div className="message-body">Failed: {status.error}</div>
              </article>
            )}
          </Form>
        )}
      </Formik>
    </Modal2>
  )
}

export default ChangeKitSkuModal
